import { useEffect, useState } from "react";
import { Dimensions } from "react-native";

// Window sizes, in points/dp:
// - "phone": phones in either orientation (a phone on its side is under 500 tall).
//   This is the original single-column layout and must stay exactly as it was.
// - "split": unfolded foldables (Galaxy Z Fold ≈ 690 wide), iPads, laptop windows.
//   Event settings show their menu on the left and the open setting on the right.
// - "modal": big monitors and 13" iPads in landscape. The event opens in a centred
//   modal over the blurred dashboard so close, back and header buttons stay near
//   the content instead of in the screen's corners.
export const SPLIT_MIN_WIDTH = 680;
export const SPLIT_MIN_HEIGHT = 500;
export const MODAL_MIN_WIDTH = 1280;
export const MODAL_MIN_HEIGHT = 760;

export const getLayoutMode = ({ width, height }) => {
  if (width < SPLIT_MIN_WIDTH || height < SPLIT_MIN_HEIGHT) return "phone";
  if (width >= MODAL_MIN_WIDTH && height >= MODAL_MIN_HEIGHT) return "modal";
  return "split";
};

// Re-renders only when the mode changes, not on every resize step.
export const useLayoutMode = () => {
  const [mode, setMode] = useState(() =>
    getLayoutMode(Dimensions.get("window"))
  );
  useEffect(() => {
    const update = ({ window }) => setMode(getLayoutMode(window));
    const subscription = Dimensions.addEventListener("change", update);
    update({ window: Dimensions.get("window") });
    return () => subscription?.remove();
  }, []);
  return mode;
};

const MODAL_MAX_HEIGHT = 880;
const MODAL_MARGIN_X = 48;
const MODAL_MARGIN_Y = 32;

// The modal's box inside the window (insets are the device's safe area).
export const getModalFrame = (
  { width, height },
  insets,
  maxWidth,
  maxHeight = MODAL_MAX_HEIGHT
) => {
  const left = insets.left || 0;
  const top = insets.top || 0;
  const availableWidth = width - left - (insets.right || 0);
  const availableHeight = height - top - (insets.bottom || 0);
  const frameWidth = Math.min(maxWidth, availableWidth - MODAL_MARGIN_X * 2);
  const frameHeight = Math.min(maxHeight, availableHeight - MODAL_MARGIN_Y * 2);
  return {
    left: Math.round(left + (availableWidth - frameWidth) / 2),
    top: Math.round(top + (availableHeight - frameHeight) / 2),
    width: Math.round(frameWidth),
    height: Math.round(frameHeight),
  };
};

// The event menu column: narrow enough to leave a phone-width pane on an
// unfolded foldable, wider on tablets and laptops.
export const getSidebarWidth = (frameWidth) =>
  Math.round(Math.min(340, Math.max(272, frameWidth * 0.3)));
